import React, { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Import brand logos
import logo1 from "../../../assest/images/brand1.png";
import logo2 from "../../../assest/images/brand2.png";
import logo3 from "../../../assest/images/brand3.png";
import logo4 from "../../../assest/images/brand4.png";
import logo5 from "../../../assest/images/brand5.png";
import logo6 from "../../../assest/images/brand6.png";
import logo7 from "../../../assest/images/brand7.png";
import logo8 from "../../../assest/images/brand8.png";

const brands = [
  { id: 1, image: logo1 },
  { id: 2, image: logo2 },
  { id: 3, image: logo3 },
  { id: 4, image: logo4 },
  { id: 5, image: logo5 },
  { id: 6, image: logo6 },
  { id: 7, image: logo7 },
  { id: 8, image: logo8 },
];

// Same hand-drawn blob used across the redesigned hero, kept subtle here.
const BLOB_D =
  "M70,8 C102,2 128,24 138,54 C148,84 132,112 102,124 C72,136 34,128 20,98 C6,68 26,26 70,8 Z";

const Brandname = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  // pauseOnHover fires on the "mouseenter" a touch tap generates, so on
  // mobile a single tap/scroll-touch was freezing the marquee like a hover
  // state. Only enable pause-on-hover for devices that actually have a
  // real hover-capable pointer (a mouse) — touch devices never match this.
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setCanHover(mq.matches);
    const handler = (e) => setCanHover(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <section
      ref={ref}
      className="relative w-full py-14 md:py-20 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(80% 120% at 0% 0%, #FFFBF0 0%, transparent 55%), radial-gradient(80% 120% at 100% 100%, #FFF3E0 0%, transparent 55%), #FFFBF5",
      }}
    >
      {/* faint ambient blobs, echoing the hero without competing with it */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 -left-10 w-56 h-56 opacity-[0.06] -z-0 hidden md:block"
        viewBox="0 0 150 140"
      >
        <path d={BLOB_D} fill="#D97706" />
      </svg>
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 -right-10 w-56 h-56 opacity-[0.06] -z-0 hidden md:block"
        viewBox="0 0 150 140"
      >
        <path d={BLOB_D} fill="#EA580C" />
      </svg>

      {/* Section Header */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        className="container mx-auto px-6 mb-8 md:mb-10 relative flex justify-center"
      >
        <div className="flex items-center w-full max-w-3xl gap-4">
          <span
            className="hidden sm:inline-block h-[2px] flex-1"
            style={{
              background: "linear-gradient(to left, #EA580C, transparent)",
            }}
          />
          <span className="text-[11px] sm:text-xs font-bold tracking-[0.15em] uppercase text-[#92400E] whitespace-nowrap">
            Trusted By Industry Leaders
          </span>
          <span
            className="hidden sm:inline-block h-[2px] flex-1"
            style={{
              background: "linear-gradient(to right, #EA580C, transparent)",
            }}
          />
        </div>
      </motion.div>

      {/* Marquee Shelf */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative container mx-auto px-4 sm:px-6"
      >
        <div
          className="relative bg-white/70 backdrop-blur-md border border-[#F5E8D5] shadow-[0_20px_45px_-24px_rgba(22,34,27,0.25)] py-6 md:py-8"
          style={{ borderRadius: "2.4rem 1rem 2.4rem 1rem" }}
        >
          {/* Seamless edge fade masks, matched to this panel's own background */}
          <div className="absolute inset-y-0 left-0 w-16 md:w-28 bg-gradient-to-r from-[#FFFBF5] via-[#FFFBF5]/70 to-transparent z-10 pointer-events-none rounded-l-[2.4rem]" />
          <div className="absolute inset-y-0 right-0 w-16 md:w-28 bg-gradient-to-l from-[#FFFBF5] via-[#FFFBF5]/70 to-transparent z-10 pointer-events-none rounded-r-[2.4rem]" />

          <Marquee gradient={false} speed={40} pauseOnHover={canHover}>
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="flex items-center justify-center mx-8 md:mx-12 lg:mx-16"
              >
                <img
                  src={brand.image}
                  alt="Partner brand"
                  className="w-20 md:w-28 lg:w-32 h-14 md:h-16 object-contain grayscale-0 opacity-100 hover:scale-105 transition-all duration-300"
                />
              </div>
            ))}
          </Marquee>
        </div>
      </motion.div>
    </section>
  );
};

export default Brandname;